import { ChangeEvent, useEffect, useState } from "react";

import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { InputGeneral } from "components/Input";

interface CustomerProcedureDeadlineProps {
  editedValue: string;
  setEditedValue: (value: string) => void;
  fieldError: boolean;
  label: string;
}

const DEFAULT_DEADLINE_VALUE = "1";

const normalizeDeadlineValue = (value: string) =>
  value && value !== "0" ? value.replace(/\D/g, "") : "";

const CustomerProcedureDeadline = ({
  editedValue,
  setEditedValue,
  fieldError,
  label,
}: CustomerProcedureDeadlineProps) => {
  const { t } = useTranslation();
  const initialDeadlineValue = normalizeDeadlineValue(editedValue);
  const [deadlineValue, setDeadlineValue] = useState<string>(() =>
    editedValue === "0" ? "0" : initialDeadlineValue,
  );
  const [lastDefinedValue, setLastDefinedValue] = useState<string>(
    initialDeadlineValue || DEFAULT_DEADLINE_VALUE,
  );
  const [isIndeterminate, setIsIndeterminate] = useState<boolean>(
    editedValue === "0",
  );

  useEffect(() => {
    if (editedValue === "0") {
      setIsIndeterminate(true);
      setDeadlineValue("0");
      return;
    }

    const normalizedValue = normalizeDeadlineValue(editedValue);

    setIsIndeterminate(false);
    setDeadlineValue(normalizedValue);

    if (normalizedValue) {
      setLastDefinedValue(normalizedValue);
    }
  }, [editedValue]);

  const handleDeadlineChange = (value: string) => {
    const sanitizedValue = value.replace(/\D/g, "");

    setDeadlineValue(sanitizedValue);
    if (sanitizedValue) {
      setLastDefinedValue(sanitizedValue);
    }
    setEditedValue(sanitizedValue);
  };

  const handleIndeterminateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setIsIndeterminate(checked);

    if (checked) {
      setDeadlineValue("0");
      setEditedValue("0");
      return;
    }

    setDeadlineValue(lastDefinedValue);
    setEditedValue(lastDefinedValue);
  };

  return (
    <Grid container spacing={2} sx={{ paddingTop: "8px" }}>
      <Grid item xs={12}>
        <InputGeneral
          id="CustomerProcedureDeadlineIdInput"
          error={fieldError}
          label={label}
          value={deadlineValue}
          onChange={handleDeadlineChange}
          required={true}
          disabled={isIndeterminate}
          type="number"
          width={381}
          maxLength={499}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isIndeterminate}
              onChange={handleIndeterminateChange}
              sx={{
                color: "#cbcbcb",
                "&.Mui-checked": {
                  color: "#1976d2",
                },
              }}
            />
          }
          label={t(
            "systemConfiguration.customerProcedureDeadline.indeterminate",
          )}
          sx={{
            margin: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: "16px",
              color: "#202020",
            },
          }}
        />
      </Grid>
    </Grid>
  );
};

export default CustomerProcedureDeadline;
